import React, { useState } from 'react';
import { AlertTriangle, ExternalLink, Pencil, RotateCcw } from 'lucide-react';
import EditLegalCaseModal from './EditLegalCaseModal';
import { formatRupees } from './budget';
import {
    ActionParty,
    CaseStatus,
    canEditLegalCase,
    caseStatusChipClass,
    formatDay,
    reopenedLabel,
    riskChipClass
} from './legalCase';
import { useProjectUserOptions } from './legalCaseOptions';

const Detail = ({ label, children, wide = false }) => (
    <div className={wide ? 'sm:col-span-2 lg:col-span-3' : ''}>
        <div className="text-gray-500">{label}</div>
        <div className="mt-0.5 text-gray-900 whitespace-pre-wrap break-words">{children || '---'}</div>
    </div>
);

const daysLabel = (days) =>
    days == null ? '' : days === 0 ? 'today' : `in ${days} day${days === 1 ? '' : 's'}`;

/**
 * The case's hearing and follow-up dates, all calculated by the API, as short
 * items for the task header beside the owner and target date.
 */
export const LegalCaseHeaderFacts = ({ legalCase }) => {
    if (!legalCase) return null;
    const facts = [
        {
            label: 'Next hearing',
            value: formatDay(legalCase.nextHearing),
            note: [legalCase.nextHearingTime, daysLabel(legalCase.daysToNextHearing)]
                .filter(Boolean)
                .join(' · ')
        },
        { label: 'Last hearing', value: formatDay(legalCase.lastHearing) },
        {
            label: 'Next follow-up',
            value: formatDay(legalCase.nextFollowUpDue),
            note: legalCase.followUpStatus ? `latest ${legalCase.followUpStatus}` : ''
        }
    ];
    return (
        <>
            {facts.map((fact) => (
                <span key={fact.label} className="flex items-center gap-2">
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-500">{fact.label}</span>
                    <span className="font-medium">{fact.value}</span>
                    {fact.note && <span className="text-sm text-gray-500">({fact.note})</span>}
                </span>
            ))}
        </>
    );
};

/**
 * The Case tab: the matter itself and what needs attention. Its dates sit in
 * the task header (LegalCaseHeaderFacts).
 */
const LegalCasePanel = ({ task }) => {
    const [isEditOpen, setIsEditOpen] = useState(false);
    const people = useProjectUserOptions(task?.project_id);
    const legalCase = task?.legalCase;

    if (!legalCase) {
        return (
            <div className="rounded-2xl border border-dashed border-[#C7D3DE] bg-white p-10 text-center text-gray-500">
                Court case details were not found for this task.
            </div>
        );
    }

    const pendingFrom =
        legalCase.actionPendingFrom === ActionParty.INTERNAL
            ? people.options.find(
                  (option) => String(option.value) === String(legalCase.actionPendingUserId)
              )?.label || ActionParty.INTERNAL
            : legalCase.actionPendingFrom;

    const attention = [
        legalCase.escalation &&
            (legalCase.escalationReason
                ? `Escalation needed: ${legalCase.escalationReason}`
                : 'Escalation needed'),
        legalCase.actionOverdue && `Target date ${formatDay(legalCase.targetDate)} has passed`,
        legalCase.hearingDocsNotReady && 'Hearing within 7 days and documents are not ready',
        legalCase.outcomePending > 0 &&
            `${legalCase.outcomePending} past hearing${legalCase.outcomePending === 1 ? '' : 's'} with no outcome recorded`,
        legalCase.followUpDue && `Follow-up due ${formatDay(legalCase.nextFollowUpDue)}`
    ].filter(Boolean);

    return (
        <div className="space-y-5">
            {isEditOpen && <EditLegalCaseModal task={task} onClose={() => setIsEditOpen(false)} />}

            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-5 space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-lg font-semibold text-gray-900">{legalCase.caseCode}</span>
                        <span
                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${caseStatusChipClass(legalCase.caseStatus)}`}>
                            {legalCase.caseStatus}
                        </span>
                        <span
                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${riskChipClass(legalCase.riskLevel)}`}>
                            {legalCase.riskLevel} risk
                        </span>
                    </div>
                    {canEditLegalCase(task) && (
                        <button
                            type="button"
                            onClick={() => setIsEditOpen(true)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#E5EAF1] bg-white px-3 py-1.5 text-sm font-medium text-[#346C75] shadow-sm transition-colors hover:bg-[#F7FAFB]"
                        >
                            <Pencil size={14} strokeWidth={2} />
                            Edit case
                        </button>
                    )}
                </div>

                {attention.length > 0 && (
                    <ul className="space-y-1 rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        {attention.map((line) => (
                            <li key={line} className="flex items-start gap-2">
                                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                                <span>{line}</span>
                            </li>
                        ))}
                    </ul>
                )}

                {legalCase.reopened && (
                    <div className="flex items-start gap-2 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                        <RotateCcw size={16} className="mt-0.5 shrink-0" />
                        <div className="min-w-0">
                            <div className="font-semibold">{reopenedLabel(legalCase.reopened)}</div>
                            <div className="mt-0.5 whitespace-pre-wrap break-words">
                                {legalCase.reopened.reason}
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <Detail label="Case type">{legalCase.caseType}</Detail>
                    <Detail label="Court / Authority">{legalCase.court}</Detail>
                    <Detail label="Case no.">{legalCase.caseNumber}</Detail>
                    <Detail label="Party / Opponent">{legalCase.opponent}</Detail>
                    <Detail label="Advocate">{legalCase.advocate}</Detail>
                    <Detail label="Filing date">
                        {legalCase.filingDate && formatDay(legalCase.filingDate)}
                    </Detail>
                    <Detail label="Current stage">{legalCase.currentStage}</Detail>
                    <Detail label="Action pending from">{pendingFrom}</Detail>
                    <Detail label="Target date">
                        {legalCase.targetDate && formatDay(legalCase.targetDate)}
                    </Detail>
                    <Detail label="Next action" wide>
                        {legalCase.nextAction}
                    </Detail>
                    <Detail label="Estimated exposure">
                        {legalCase.exposureAmount != null && formatRupees(legalCase.exposureAmount)}
                    </Detail>
                    <Detail label="Legal fees pending">
                        {legalCase.legalFeesPending != null && formatRupees(legalCase.legalFeesPending)}
                    </Detail>
                    <Detail label="Document folder">
                        {legalCase.folderLink && (
                            <a
                                href={legalCase.folderLink}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-[#346C75] underline">
                                Open folder
                                <ExternalLink size={14} />
                            </a>
                        )}
                    </Detail>
                    <Detail label="Latest update / remarks" wide>
                        {legalCase.latestRemarks}
                    </Detail>
                    {legalCase.caseStatus === CaseStatus.CLOSED && (
                        <Detail
                            label={`Closing note${legalCase.closedAt ? ` · closed ${formatDay(legalCase.closedAt)}` : ''}`}
                            wide>
                            {legalCase.closingNote}
                        </Detail>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LegalCasePanel;
